/**
 * Type definitions for Service Provider System
 */

// Service Provider Type
export type ServiceProviderType =
  | 'Videographer'
  | 'Photographer'
  | 'Stylist'
  | 'Product'
  | 'Location'
  | 'Equipment'
  | 'Other';

// Service Status
export type ServiceStatus =
  | 'Requested'
  | 'Confirmed'
  | 'In Progress'
  | 'Completed'
  | 'Cancelled';

// Billing Interval for recurring services
export type BillingInterval = 'monthly' | 'quarterly' | 'semi_annual' | 'yearly' | 'custom';

// ============================================================================
// Database Table Interfaces
// ============================================================================

export interface ServiceProvider {
  id: string;
  name: string;
  type: ServiceProviderType;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  vat_number: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignService {
  id: string;
  campaign_id: string;
  service_provider_id: string | null;
  service_provider_name: string;
  service_type: ServiceProviderType;
  status: ServiceStatus;
  agreed_fee: number;
  currency: string;
  description: string | null;
  notes: string | null;
  display_order: number;
  is_recurring: boolean;
  billing_interval: BillingInterval | null;
  custom_interval_days: number | null;
  recurring_start_date: string | null;
  recurring_end_date: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Insert/Update Interfaces
// ============================================================================

export interface ServiceProviderInsert {
  name: string;
  type: ServiceProviderType;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  vat_number?: string | null;
  notes?: string | null;
}

export interface ServiceProviderUpdate {
  name?: string;
  type?: ServiceProviderType;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  vat_number?: string | null;
  notes?: string | null;
}

export interface CampaignServiceInsert {
  campaign_id: string;
  service_provider_id?: string | null;
  service_provider_name: string;
  service_type: ServiceProviderType;
  status?: ServiceStatus;
  agreed_fee?: number;
  currency?: string;
  description?: string | null;
  notes?: string | null;
  display_order?: number;
  is_recurring?: boolean;
  billing_interval?: BillingInterval | null;
  custom_interval_days?: number | null;
  recurring_start_date?: string | null;
  recurring_end_date?: string | null;
}

export interface CampaignServiceUpdate {
  service_provider_name?: string;
  service_type?: ServiceProviderType;
  status?: ServiceStatus;
  agreed_fee?: number;
  currency?: string;
  description?: string | null;
  notes?: string | null;
  display_order?: number;
  is_recurring?: boolean;
  billing_interval?: BillingInterval | null;
  custom_interval_days?: number | null;
  recurring_start_date?: string | null;
  recurring_end_date?: string | null;
}

// ============================================================================
// Extended/Computed Interfaces
// ============================================================================

export interface ServiceProviderWithStats extends ServiceProvider {
  campaign_count: number;
  total_fees: number;
}

// ============================================================================
// UI Configuration
// ============================================================================

export const SERVICE_PROVIDER_TYPE_CONFIG = {
  Videographer: {
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    icon: 'Video'
  },
  Photographer: {
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    icon: 'Camera'
  },
  Stylist: {
    color: 'pink',
    bgColor: 'bg-pink-100',
    textColor: 'text-pink-800',
    icon: 'Scissors'
  },
  Product: {
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    icon: 'Package'
  },
  Location: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    icon: 'MapPin'
  },
  Equipment: {
    color: 'slate',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-800',
    icon: 'Settings'
  },
  Other: {
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    icon: 'MoreHorizontal'
  },
} as const;

export const SERVICE_STATUS_CONFIG = {
  Requested: { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', order: 0 },
  Confirmed: { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', order: 1 },
  'In Progress': { color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', order: 2 },
  Completed: { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', order: 3 },
  Cancelled: { color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800', order: -1 },
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

export type RecurringChargeStatus = 'Pending' | 'Invoiced' | 'Paid' | 'Cancelled';

export interface RecurringServiceCharge {
  id: string;
  campaign_service_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  status: RecurringChargeStatus;
  invoice_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const BILLING_INTERVAL_CONFIG: Record<BillingInterval, { label: string; labelPt: string; months: number }> = {
  monthly: { label: 'Monthly', labelPt: 'Mensal', months: 1 },
  quarterly: { label: 'Quarterly', labelPt: 'Trimestral', months: 3 },
  semi_annual: { label: 'Semi-Annual', labelPt: 'Semestral', months: 6 },
  yearly: { label: 'Yearly', labelPt: 'Anual', months: 12 },
  custom: { label: 'Custom', labelPt: 'Personalizado', months: 0 },
};

/**
 * Format service provider type for display
 */
export function formatServiceProviderType(type: ServiceProviderType): string {
  return type;
}

/**
 * Get available service provider types
 */
export function getServiceProviderTypes(): ServiceProviderType[] {
  return [
    'Videographer',
    'Photographer',
    'Stylist',
    'Product',
    'Location',
    'Equipment',
    'Other'
  ];
}

/**
 * Calculate total service costs for a campaign
 */
export function calculateTotalServiceCosts(services: CampaignService[]): number {
  return services.reduce((total, service) => total + (service.agreed_fee || 0), 0);
}
